import React, { useState } from 'react';
import { Layout, Home, User, Phone, Layers, PenTool, ShoppingBag, Package } from 'lucide-react';

const PageSelector = ({ onConfirm, onBack }) => {
  // Default: All selected
  const [selectedPages, setSelectedPages] = useState([
    'landing',
    'home',
    'about',
    'services',
    'product-catalog',
    'product-single',
    'contact',
    'brand'
  ]);

  const pages = [
    { 
      id: 'landing', 
      label: 'Landing Page', 
      icon: Layout, 
      desc: 'Marketing-focused page with hero, CTA, and conversion sections.' 
    },
    { 
      id: 'home', 
      label: 'Home Page', 
      icon: Home, 
      desc: 'Main website overview with navigation and highlights.' 
    },
    { 
      id: 'about', 
      label: 'About Us', 
      icon: User, 
      desc: 'Company history, team bios, and mission.' 
    },
    { 
      id: 'services', 
      label: 'Services / Products', 
      icon: Layers, 
      desc: 'Grid layout of offerings with pricing.' 
    },
    // 🚀 NEW: ALL PRODUCTS (THE CATALOG)
    { 
      id: 'product-catalog', 
      label: 'Product Catalog', 
      desc: 'Full e-commerce grid displaying all products with category filters and sorting.',
      icon: ShoppingBag // If you use icons in your array
    },
    
    // 🚀 NEW: SINGLE PRODUCT PAGE
    { 
      id: 'product-single', 
      label: 'Single Product Details', 
      desc: 'Dedicated page for an individual item with an image gallery, size/color selector, and Add to Cart.',
      icon: Package // If you use icons in your array
    },
    { 
      id: 'contact', 
      label: 'Contact', 
      icon: Phone, 
      desc: 'Contact form, map, and address details.' 
    },
    { 
      id: 'brand', 
      label: 'Brand Guidance', 
      icon: PenTool, 
      desc: 'Typography, color palette, and UI kit.' 
    },
  ];

  const togglePage = (id) => {
    if (selectedPages.includes(id)) {
      setSelectedPages(selectedPages.filter(p => p !== id));
    } else {
      setSelectedPages([...selectedPages, id]);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 w-full max-w-2xl mx-auto shadow-2xl">
      <h2 className="text-2xl font-bold text-white mb-2">
        Which pages do you need?
      </h2>
      <p className="text-gray-400 mb-6">
        Select the sections you want the AI to generate code for.
      </p>

      <div className="grid grid-cols-1 gap-3 mb-8">
        {pages.map((page) => {
          const isSelected = selectedPages.includes(page.id);
          const Icon = page.icon;

          return (
            <button
              key={page.id}
              onClick={() => togglePage(page.id)}
              className={`flex items-center p-4 rounded-xl border transition-all ${
                isSelected 
                  ? 'bg-blue-900/20 border-blue-500 shadow-blue-900/20 shadow-lg' 
                  : 'bg-gray-800 border-gray-700 hover:border-gray-500 opacity-70'
              }`}
            >
              <div
                className={`p-3 rounded-lg mr-4 ${
                  isSelected 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-700 text-gray-400'
                }`}
              >
                <Icon size={20} />
              </div>

              <div className="text-left flex-1">
                <h3 className={`font-bold ${
                  isSelected ? 'text-white' : 'text-gray-400'
                }`}>
                  {page.label}
                </h3>
                <p className="text-xs text-gray-500">
                  {page.desc}
                </p>
              </div>

              <div
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-600' 
                    : 'border-gray-600'
                }`}
              >
                {isSelected && (
                  <span className="text-white text-xs">✓</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex gap-4">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition"
        >
          Back
        </button>

        <button 
          onClick={() => onConfirm(selectedPages)} 
          disabled={selectedPages.length === 0}
          className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold py-3 rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Generate {selectedPages.length} Pages
        </button>
      </div>
    </div>
  );
};

export default PageSelector;